import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShoppingCartSimple, MagnifyingGlass, ArrowUpRight, Check } from "@phosphor-icons/react";
import PageShell from "@/components/site/PageShell";
import { CATEGORIES, PRODUCTS, formatUSD } from "@/data/products";
import { useCart } from "@/context/CartContext";

export default function Inventory() {
  const [activeCat, setActiveCat] = useState("all");
  const [query, setQuery] = useState("");
  const [justAdded, setJustAdded] = useState(null);
  const { addItem, count, subtotal } = useCart();

  const filtered = useMemo(() => {
    let list = PRODUCTS;
    if (activeCat !== "all") list = list.filter((p) => p.category === activeCat);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (p) => p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q)
      );
    }
    return list;
  }, [activeCat, query]);

  const grouped = useMemo(() => {
    const g = {};
    for (const p of filtered) {
      g[p.category] = g[p.category] || [];
      g[p.category].push(p);
    }
    return g;
  }, [filtered]);

  const onAdd = (p) => {
    addItem(p, 1);
    setJustAdded(p.sku);
    toast.success(`Added — ${p.name}`);
    setTimeout(() => setJustAdded((s) => (s === p.sku ? null : s)), 900);
  };

  return (
    <PageShell>
      {/* Hero band */}
      <section
        className="relative border-b border-white/10 bg-background py-16 md:py-24 overflow-hidden"
        data-testid="inventory-hero"
      >
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="mono-label text-primary">/ Inventory</span>
            <span className="mono-label text-muted-foreground">
              {PRODUCTS.length} SKUs stocked
            </span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl lg:text-8xl leading-[0.85]">
              Shop the
              <br />
              full <span className="text-primary">catalog</span>.
            </h1>
            <div className="lg:w-96">
              <label className="mono-label text-muted-foreground">Search</label>
              <div className="mt-2 flex items-center border border-white/15 bg-transparent px-4 h-12">
                <MagnifyingGlass size={18} weight="light" className="text-muted-foreground" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name or SKU..."
                  className="ml-3 w-full bg-transparent outline-none text-sm font-mono-tech placeholder:text-muted-foreground/60"
                  data-testid="inventory-search"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories filter */}
      <section className="sticky top-16 md:top-20 z-30 border-b border-white/10 bg-background/90 backdrop-blur-xl">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-4 flex items-center gap-2 overflow-x-auto no-scrollbar">
          <button
            onClick={() => setActiveCat("all")}
            className={`mono-label whitespace-nowrap px-4 py-2 border transition-colors ${
              activeCat === "all"
                ? "border-primary bg-primary text-primary-foreground"
                : "border-white/15 text-foreground/70 hover:border-primary hover:text-primary"
            }`}
            data-testid="cat-filter-all"
          >
            All ({PRODUCTS.length})
          </button>
          {CATEGORIES.map((c) => {
            const n = PRODUCTS.filter((p) => p.category === c.id).length;
            const active = activeCat === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                className={`mono-label whitespace-nowrap px-4 py-2 border transition-colors ${
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-white/15 text-foreground/70 hover:border-primary hover:text-primary"
                }`}
                data-testid={`cat-filter-${c.id}`}
              >
                {c.label} ({n})
              </button>
            );
          })}
        </div>
      </section>

      {/* Product Lists */}
      <section className="bg-background py-12 md:py-16" data-testid="inventory-list">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          {Object.keys(grouped).length === 0 && (
            <div className="border border-white/10 p-12 text-center">
              <p className="mono-label text-muted-foreground">No products found.</p>
            </div>
          )}
          {CATEGORIES.filter((c) => grouped[c.id]).map((c) => (
            <div key={c.id} className="mb-16" data-testid={`inventory-category-${c.id}`}>
              <div className="flex items-baseline justify-between mb-6 border-b border-white/10 pb-3">
                <h2 className="font-display font-bold uppercase tracking-tight text-2xl md:text-3xl">
                  {c.label}
                </h2>
                <span className="mono-label text-muted-foreground">
                  {grouped[c.id].length} items
                </span>
              </div>
              <div className="grid grid-cols-1 border-l border-r border-white/10">
                {grouped[c.id].map((p) => (
                  <ProductRow
                    key={p.sku}
                    p={p}
                    onAdd={onAdd}
                    added={justAdded === p.sku}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Sticky cart FAB */}
      {count > 0 && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="fixed bottom-6 right-6 z-40"
        >
          <Link
            to="/cart"
            className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-6 py-4 mono-label shadow-lg hover:bg-foreground hover:text-background transition-colors"
            data-testid="floating-cart-cta"
          >
            <ShoppingCartSimple size={20} weight="bold" />
            {count} item{count > 1 ? "s" : ""} · {formatUSD(subtotal)}
            <ArrowUpRight size={16} weight="bold" />
          </Link>
        </motion.div>
      )}
    </PageShell>
  );
}

function ProductRow({ p, onAdd, added }) {
  return (
    <div
      className="border-b border-white/10 grid grid-cols-12 gap-4 p-4 md:p-5 items-center hover:bg-secondary/40 transition-colors group"
      data-testid={`product-${p.sku}`}
    >
      <div className="col-span-12 md:col-span-1 font-mono-tech text-xs text-muted-foreground">
        {p.sku}
      </div>
      <div className="col-span-8 md:col-span-6">
        <p className="font-display font-medium text-sm md:text-base leading-tight">
          {p.name}
        </p>
        <p className="mono-label text-muted-foreground mt-1">{p.unit}</p>
      </div>
      <div className="col-span-4 md:col-span-3 text-right md:text-left font-mono-tech text-lg md:text-xl">
        {formatUSD(p.price)}
      </div>
      <div className="col-span-12 md:col-span-2 flex md:justify-end">
        <button
          onClick={() => onAdd(p)}
          className={`inline-flex items-center gap-2 border px-4 py-3 mono-label transition-colors ${
            added
              ? "border-primary bg-primary text-primary-foreground"
              : "border-white/15 hover:border-primary hover:text-primary"
          }`}
          data-testid={`add-to-cart-${p.sku}`}
          aria-label={`Add ${p.name} to cart`}
        >
          {added ? (
            <>
              <Check size={16} weight="bold" /> Added
            </>
          ) : (
            <>
              <ShoppingCartSimple size={16} weight="bold" /> Add
            </>
          )}
        </button>
      </div>
    </div>
  );
}
